import { useTranslate } from 'core/hooks/useTranslate';
import { useCallback, useState } from 'react';

type UseToggleVisibilityDialogProps = {
  type: 'module' | 'submodule';
  isHidden: boolean;
  onSuccess: () => void;
  onClose: () => void;
  isLoading?: boolean;
};

type UseToggleVisibilityDialogReturn = {
  t: (key: string) => string;
  title: string;
  questionText: string;
  checkboxLabel: string;
  disclaimerText: string;
  buttonText: string;
  isChecked: boolean;
  handleCheckboxChange: () => void;
  handleConfirm: () => void;
  handleClose: () => void;
  isDisabled: boolean;
};

function useToggleVisibilityDialog({ type, isHidden, onClose, onSuccess, isLoading = false }: UseToggleVisibilityDialogProps): UseToggleVisibilityDialogReturn {
  const { t } = useTranslate();
  const [isChecked, setIsChecked] = useState(false);
  const isModule = type === 'module';

  const title = isHidden ? t('organize-content:Show content') : t('organize-content:Hide content');
  const questionText = isHidden
    ? isModule
      ? t('organize-content:Show module confirmation question')
      : t('organize-content:Show submodule confirmation question')
    : isModule
      ? t('organize-content:Hide module confirmation question')
      : t('organize-content:Hide submodule confirmation question');
  const checkboxLabel = t('organize-content:Yes I am sure');
  const disclaimerText = t('organize-content:Toggle visibility disclaimer');
  const buttonText = isHidden ? t('organize-content:Yes, show') : t('organize-content:Yes, hide');

  const handleCheckboxChange = useCallback(() => {
    setIsChecked((prev) => !prev);
  }, []);

  const handleConfirm = useCallback(() => {
    if (isChecked) {
      onSuccess();
      setIsChecked(false);
    }
  }, [isChecked, onSuccess]);

  const handleClose = useCallback(() => {
    setIsChecked(false);
    onClose();
  }, [onClose]);

  const isDisabled = !isChecked || isLoading;

  return {
    t,
    title,
    questionText,
    checkboxLabel,
    disclaimerText,
    buttonText,
    isChecked,
    handleCheckboxChange,
    handleConfirm,
    handleClose,
    isDisabled,
  };
}

export default useToggleVisibilityDialog;

